<div class="bn-review">
  <p-confirmDialog [appendTo]="'body'" />
  <header class="bn-review__summary">
    <p class="bn-review__summary-counts" data-testid="review-summary">
      {{ summaryLabel() }}
    </p>
    <div class="bn-review__summary-right">
      <span class="bn-review__total" data-testid="review-total">{{
        countLabel()
      }}</span>
      <p-button
        icon="pi pi-refresh"
        severity="secondary"
        [text]="true"
        [rounded]="true"
        size="small"
        [loading]="loading()"
        ariaLabel="Refresh activities"
        pTooltip="Refresh"
        tooltipPosition="left"
        (onClick)="refresh()"
      />
    </div>
  </header>

  <div class="bn-filters" role="group" aria-label="Review filters">
    <div class="bn-filters__primary">
      <div class="bn-filters__field">
        <label class="bn-filters__label" for="reviewStatusFilter">Status</label>
        <p-select
          inputId="reviewStatusFilter"
          [options]="statusFilterOptions"
          [ngModel]="statusFilter()"
          (ngModelChange)="onStatusFilterChange($event)"
          optionLabel="label"
          optionValue="value"
          [appendTo]="'body'"
          size="small"
          name="reviewStatusFilter"
          styleClass="bn-filter-select"
          panelStyleClass="bn-filter-overlay"
          ariaLabel="Filter by status"
          data-testid="review-filter-status"
        />
      </div>

      <div class="bn-filters__field bn-filters__field--action">
        <label class="bn-filters__label" for="reviewActionFilter">Action</label>
        <p-select
          inputId="reviewActionFilter"
          [options]="actionFilterOptions"
          [ngModel]="actionTypeFilter()"
          (ngModelChange)="onActionTypeFilterChange($event)"
          optionLabel="label"
          optionValue="value"
          [appendTo]="'body'"
          size="small"
          name="reviewActionFilter"
          styleClass="bn-filter-select"
          panelStyleClass="bn-filter-overlay"
          ariaLabel="Filter by action"
          data-testid="review-filter-action"
        />
      </div>

      @if (hasFilters) {
        <button
          type="button"
          class="bn-filters__reset"
          data-testid="review-filter-reset"
          (click)="resetFilters()"
        >
          Reset
        </button>
      }
    </div>

    <div class="bn-filters__field bn-filters__field--group">
      <label class="bn-filters__label" for="reviewGroupBy">Group by</label>
      <p-select
        inputId="reviewGroupBy"
        [options]="groupByOptions"
        [ngModel]="groupBy()"
        (ngModelChange)="onGroupByChange($event)"
        optionLabel="label"
        optionValue="value"
        [appendTo]="'body'"
        size="small"
        name="reviewGroupBy"
        styleClass="bn-filter-select"
        panelStyleClass="bn-filter-overlay"
        ariaLabel="Group activities"
        data-testid="review-filter-group-by"
      />
    </div>
  </div>

  @if (countsCaveat(); as caveat) {
    <p class="bn-review__caveat" data-testid="review-caveat">
      <i class="pi pi-info-circle"></i>
      {{ caveat }}
    </p>
  }

  @if (loading()) {
    <div class="bn-list" data-testid="review-loading">
      @for (row of [1, 2, 3, 4]; track row) {
        <div class="bn-row bn-row--skeleton">
          <div class="flex flex-col gap-2 flex-1 min-w-0">
            <p-skeleton width="22rem" height="1rem" />
            <p-skeleton width="30rem" height="0.75rem" />
            <p-skeleton width="6rem" height="0.625rem" />
          </div>
          <p-skeleton width="8rem" height="1.25rem" />
        </div>
      }
    </div>
  } @else if (error(); as errorMessage) {
    <div class="bn-review__error" data-testid="review-error">
      <p class="bn-review__error-title">
        <i class="pi pi-exclamation-triangle"></i>
        Could not load activities
      </p>
      <p class="bn-review__error-detail">{{ errorMessage }}</p>
      <p-button
        label="Try again"
        size="small"
        severity="secondary"
        [outlined]="true"
        (onClick)="refresh()"
      />
    </div>
  } @else if (visible().length === 0) {
    <div class="bn-review__empty" data-testid="review-empty">
      <i class="pi pi-inbox"></i>
      @if (totalCount() === 0) {
        <p>Nothing in the review queue.</p>
        <p class="bn-review__empty-hint">
          Activities appear here once the conversation router agent proposes
          actions for a live conversation.
        </p>
      } @else {
        <p>No activities match the selected filters.</p>
        <p-button
          label="Clear filters"
          size="small"
          [text]="true"
          (onClick)="resetFilters()"
        />
      }
    </div>
  } @else {
    <ul class="bn-list" data-testid="review-list">
      @for (row of visibleRows(); track trackRow(row)) {
        @if (row.kind === 'group') {
          <li class="bn-group" [attr.data-testid]="'review-group-' + row.id">
            {{ row.label }}
          </li>
        } @else {
          <li>
            <div
              class="bn-row"
              [attr.data-status]="row.activity.status"
              [attr.data-testid]="'review-row-' + row.activity.activity_id"
            >
              <div class="bn-row__body">
                <div class="bn-row__heading">
                  @if (isNew(row.activity)) {
                    <span
                      class="bn-row__unseen"
                      pTooltip="Not opened yet"
                      tooltipPosition="top"
                    ></span>
                  }
                  <h3 class="bn-row__title" [attr.title]="row.activity.title">
                    <button
                      type="button"
                      class="bn-row__open"
                      [attr.data-testid]="
                        'review-row-open-' + row.activity.activity_id
                      "
                      (click)="openActivity(row.activity)"
                    >
                      {{ row.activity.title }}
                    </button>
                  </h3>
                  <span class="bn-row__heading-meta">
                    <span
                      class="bn-row__action-count"
                      [pTooltip]="actionCountTooltipFor(row.activity)"
                      tooltipPosition="top"
                      >{{ actionCountFor(row.activity) }}</span
                    >
                    @if (since(row.activity.created_at); as relative) {
                      <span
                        class="bn-row__time"
                        [pTooltip]="stamp(row.activity.created_at)"
                        tooltipPosition="top"
                        tooltipStyleClass="bn-stamp-tooltip"
                        data-testid="review-row-time"
                        >{{ relative }}</span
                      >
                    }
                  </span>
                </div>

                @if (actionTitlesFor(row.activity); as titles) {
                  <ol class="bn-row__actions-list">
                    @for (title of titles; track $index) {
                      <li>{{ title }}</li>
                    }
                  </ol>
                }
              </div>

              <div class="bn-row__side">
                @if (!canActOnQueue(row.activity)) {
                  <span
                    class="bn-status"
                    [ngClass]="statusMeta[row.activity.status].chipClass"
                  >
                    <span
                      class="bn-status__dot"
                      [ngClass]="statusMeta[row.activity.status].dotClass"
                    ></span>
                    {{ statusMeta[row.activity.status].label }}
                  </span>
                }
                @if (canActOnQueue(row.activity)) {
                  <div class="bn-row__cta-group">
                    <p-button
                      label="Confirm"
                      size="small"
                      [loading]="busyActivityId() === row.activity.activity_id"
                      [disabled]="busyActivityId() !== null"
                      data-testid="review-row-confirm"
                      (onClick)="confirmFromRow($event, row.activity)"
                    />
                    <p-button
                      label="Deny"
                      size="small"
                      severity="danger"
                      [outlined]="true"
                      [disabled]="busyActivityId() !== null"
                      data-testid="review-row-deny"
                      (onClick)="denyFromRow($event, row.activity)"
                    />
                  </div>
                }
              </div>
            </div>
          </li>
        }
      }
    </ul>

    <footer class="bn-pager">
      <span class="bn-pager__range" data-testid="review-range">{{
        rangeLabel()
      }}</span>
      @if (pageCount() > 1) {
        <div class="bn-pager__controls">
          <button
            type="button"
            class="bn-pager__step"
            [disabled]="page() === 0"
            (click)="goToPage(page() - 1)"
          >
            <i class="pi pi-angle-left"></i> Previous
          </button>
          @for (index of pageNumbers(); track index) {
            <button
              type="button"
              class="bn-pager__page"
              [class.bn-pager__page--active]="index === page()"
              [attr.aria-current]="index === page() ? 'page' : null"
              (click)="goToPage(index)"
            >
              {{ index + 1 }}
            </button>
          }
          <button
            type="button"
            class="bn-pager__step"
            [disabled]="page() === pageCount() - 1"
            (click)="goToPage(page() + 1)"
          >
            Next <i class="pi pi-angle-right"></i>
          </button>
        </div>
      }
    </footer>
  }
</div>

@if (selectedActivity(); as activity) {
  <bn-activity-detail-dialog
    [activity]="activity"
    [clientName]="clientName()"
    [actionDescriptions]="actionDescriptions()"
    [visible]="detailVisible()"
    (visibleChange)="detailVisible.set($event)"
    (changed)="onActivityChanged()"
  />
}
